import { Link } from 'react-router-dom';
import Navigation from './components/Navigation';
import ModuleCard from './components/ModuleCard';
import Footer from '../home/components/Footer';
import { useState } from 'react';

export default function HighPage() {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');

  const modules = [
    {
      id: 'llm-tuning',
      icon: 'ri-settings-3-line',
      title: 'LLM 深度调优台',
      description: '调整大模型超参数,观察生成效果变化',
      color: 'purple',
      features: ['参数可视化', '效果对比', '性能分析'],
      difficulty: 'advanced'
    },
    {
      id: 'python-ide',
      icon: 'ri-code-s-slash-line',
      title: 'Python 云端编程 IDE',
      description: '在线编写AI代码,构建真实应用原型',
      color: 'blue',
      features: ['Jupyter风格', '预装AI库', '云端运行'],
      difficulty: 'core'
    },
    {
      id: 'ethics-debate',
      icon: 'ri-scales-line',
      title: '数据伦理辩论场',
      description: '发现并修正数据偏见,培养AI伦理意识',
      color: 'red',
      features: ['偏见检测', '数据清洗', '模型修正'],
      difficulty: 'advanced'
    },
    {
      id: 'model-training',
      icon: 'ri-bar-chart-box-line',
      title: '模型训练工作站',
      description: '从数据准备到模型部署的完整流程',
      color: 'green',
      features: ['数据标注', '模型训练', '效果评估'],
      difficulty: 'core'
    },
    {
      id: 'research-incubator',
      icon: 'ri-flask-line',
      title: '科研项目孵化器',
      description: '基于真实问题,开展AI研究项目',
      color: 'orange',
      features: ['课题选择', '实验设计', '论文撰写'],
      difficulty: 'advanced'
    },
    {
      id: 'industry-case',
      icon: 'ri-building-line',
      title: '产业案例研习室',
      description: '学习AI在各行业的实际应用案例',
      color: 'indigo',
      features: ['真实案例', '技术拆解', '实践模拟'],
      difficulty: 'basic'
    }
  ];

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');

    if (!loginForm.username || !loginForm.password) {
      setLoginError('请输入学号和密码');
      return;
    }

    if (loginForm.username.length < 4) {
      setLoginError('学号格式不正确');
      return;
    }

    alert('登录功能正在开发中，请联系学校管理员获取账号信息');
    setShowLoginModal(false);
    setLoginForm({ username: '', password: '' });
  };

  const handleContactSupport = () => {
    setShowLoginModal(false);
    const vapiButton = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (vapiButton) {
      vapiButton.click();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-purple-100 text-purple-700 px-6 py-3 rounded-full mb-8">
            <i className="ri-microscope-line text-xl"></i>
            <span className="font-semibold">高中版块 · 探究与创新</span>
          </div>
          
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            AI 探究与工程创新
          </h1>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12 leading-relaxed">
            掌握大模型架构,进行小规模项目开发,培养AI工程思维和创新能力,为未来深造和职业发展奠定基础
          </p>

          <div className="flex flex-wrap justify-center gap-6 mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-6 w-64">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-search-line text-3xl text-purple-600"></i>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">探究</div>
              <div className="text-gray-600">深入技术原理</div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 w-64">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-lightbulb-line text-3xl text-blue-600"></i>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">创新</div>
              <div className="text-gray-600">开发实际应用</div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-6 w-64">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-links-line text-3xl text-indigo-600"></i>
              </div>
              <div className="text-3xl font-bold text-gray-900 mb-2">融通</div>
              <div className="text-gray-600">跨学科整合</div>
            </div>
          </div>
        </div>
      </section>

      {/* Modules Grid */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">六大专业模块</h2>
            <p className="text-xl text-gray-600">从理论研究到工程实践,全方位培养AI专业能力</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {modules.map((module) => (
              <ModuleCard key={module.id} {...module} />
            ))}
          </div>
        </div>
      </section>

      {/* Core Competencies */}
      <section className="py-20 px-6 bg-gradient-to-r from-purple-50 to-indigo-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">核心能力培养</h2>
            <p className="text-xl text-gray-600">对标浙江省AI教育高中阶段要求</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: 'ri-cpu-line',
                title: '技术深度',
                items: ['掌握大语言模型架构', '理解Transformer原理', '熟悉常见AI算法', '掌握模型调优方法']
              },
              {
                icon: 'ri-code-box-line',
                title: '工程能力',
                items: ['Python编程实践', '数据处理与分析', '模型训练与部署', '项目开发经验']
              },
              {
                icon: 'ri-scales-3-line',
                title: '伦理素养',
                items: ['辨析数据偏见', '识别算法歧视', '理解隐私保护', '遵守AI伦理规范']
              },
              {
                icon: 'ri-lightbulb-flash-line',
                title: '创新思维',
                items: ['问题发现能力', '跨学科整合', '创新解决方案', '批判性思考']
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-xl p-6">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl flex items-center justify-center mb-4">
                  <i className={`${item.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{item.title}</h3>
                <ul className="space-y-2">
                  {item.items.map((subItem, idx) => (
                    <li key={idx} className="flex items-start space-x-2 text-sm text-gray-700">
                      <i className="ri-checkbox-circle-fill text-purple-600 mt-0.5"></i>
                      <span>{subItem}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Path */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">未来发展路径</h2>
            <p className="text-xl text-gray-600">为大学深造和职业发展做好准备</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-school-line',
                title: '学术深造',
                description: '为计算机科学、人工智能等专业打下坚实基础',
                paths: ['AI研究方向', '机器学习专业', '数据科学领域', '计算机视觉']
              },
              {
                icon: 'ri-briefcase-line',
                title: '职业发展',
                description: '掌握AI工程师必备的核心技能',
                paths: ['算法工程师', 'AI产品经理', '数据分析师', 'ML工程师']
              },
              {
                icon: 'ri-rocket-line',
                title: '创新创业',
                description: '具备AI应用开发和创新项目孵化能力',
                paths: ['AI创业项目', '技术创新竞赛', '开源项目贡献', '科研课题研究']
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-xl p-8 border-2 border-gray-100 hover:border-purple-300 transition-all">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-2xl flex items-center justify-center mb-6">
                  <i className={`${item.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 mb-6">{item.description}</p>
                <div className="space-y-2">
                  {item.paths.map((path, idx) => (
                    <div key={idx} className="flex items-center space-x-2 text-gray-700">
                      <i className="ri-arrow-right-s-line text-purple-600"></i>
                      <span className="text-sm">{path}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-purple-500 to-indigo-500 rounded-3xl shadow-2xl p-12 text-white">
            <i className="ri-graduation-cap-line text-6xl mb-6"></i>
            <h2 className="text-4xl font-bold mb-6">开启AI专业学习之旅</h2>
            <p className="text-xl mb-8 opacity-90">
              登录后即可访问所有专业模块,从理论到实践全面提升AI工程能力
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button 
                onClick={() => setShowLoginModal(true)}
                className="px-8 py-4 bg-white text-purple-600 rounded-lg font-bold hover:bg-gray-100 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                学生登录
              </button>
              <Link to="/" className="px-8 py-4 bg-purple-700 text-white rounded-lg font-bold hover:bg-purple-800 transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer inline-block">
                返回首页
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Login Modal */}
      {showLoginModal && (
        <div 
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowLoginModal(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-purple-500 to-indigo-500 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-user-star-line text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold">高中生登录</h3>
                    <p className="text-white/80 text-sm">探究AI工程与创新</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowLoginModal(false)}
                  className="w-8 h-8 flex items-center justify-center hover:bg-white/20 rounded-full transition-all cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>

            <form onSubmit={handleLogin} className="p-6 space-y-5">
              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm">
                  <i className="ri-user-line mr-2"></i>学号
                </label>
                <input
                  type="text"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                  placeholder="请输入你的学号"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none transition-all text-sm"
                />
              </div>

              <div>
                <label className="block text-gray-700 font-semibold mb-2 text-sm">
                  <i className="ri-lock-line mr-2"></i>密码
                </label>
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  placeholder="请输入密码"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-purple-500 focus:outline-none transition-all text-sm"
                />
              </div>

              {loginError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm flex items-center">
                  <i className="ri-error-warning-line mr-2"></i>
                  {loginError}
                </div>
              )}

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <i className="ri-information-line text-purple-600 text-xl mt-0.5"></i>
                  <div className="text-sm text-purple-800">
                    <p className="font-semibold mb-1">温馨提示：</p>
                    <ul className="space-y-1 text-purple-700">
                      <li>• 学号由学校统一分配</li>
                      <li>• 初始密码为身份证后6位</li>
                      <li>• 如忘记密码，请联系班主任</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-lg font-bold hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-login-box-line mr-2"></i>
                  立即登录
                </button>
                <button
                  type="button"
                  onClick={handleContactSupport}
                  className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-customer-service-2-line mr-2"></i>
                  联系老师
                </button>
              </div>

              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowLoginModal(false);
                    window.REACT_APP_NAVIGATE('/');
                  }}
                  className="text-sm text-gray-500 hover:text-purple-600 transition-all cursor-pointer"
                >
                  返回首页
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
